import { formatCurrency } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import { PaymentStatusBadge } from "@bidbattles/bloks/components/payments";
import { Button } from "@bidbattles/bloks/components/ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogTitle,
	DialogTrigger,
} from "@bidbattles/bloks/components/ui/dialog";
import { DataTable } from "@bidbattles/bloks/components/ui/table";
import {
	BBBackendQueryOptions,
	type PaymentDetails,
	type PaymentItem,
} from "@bidbattles/bloks/lib/rapace";
import { paginationSearchParamsSchema } from "@bidbattles/bloks/lib/rapace/utils";
import { cn } from "@bidbattles/bloks/lib/ui";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo } from "react";

export const Route = createFileRoute("/settings/billing-history")({
	loaderDeps: ({ search }) => ({ pagination: search }),
	async loader({ context, deps: { pagination } }) {
		await context.queryClient.ensureQueryData(
			BBBackendQueryOptions.listPayments(pagination),
		);
	},
	validateSearch: zodValidator(paginationSearchParamsSchema),
	component: RouteComponent,
	ssr: false,
});

const paymentColumnHelper = createColumnHelper<PaymentDetails>();
const paymentItemColumnHelper = createColumnHelper<PaymentItem>();

function RouteComponent() {
	const paginationSearchParams = Route.useSearch();
	const {
		data: { payments, pagination },
	} = useSuspenseQuery(
		BBBackendQueryOptions.listPayments(paginationSearchParams),
	);

	const paymentColumns = useMemo(
		() => [
			paymentColumnHelper.accessor("provider_checkout_id", {
				header: "Provider Reference",
			}),

			paymentColumnHelper.accessor("status", {
				header: "Status",
				cell: (info) => {
					return <PaymentStatusBadge status={info.getValue()} />;
				},
			}),

			paymentColumnHelper.accessor("amount", {
				header: "Amount",
				cell: (info) => {
					return formatCurrency("EUR", info.getValue());
				},
			}),

			paymentColumnHelper.accessor("created_at", {
				header: () => (
					<span className="text-end w-full inline-block">Date</span>
				),
				cell: (info) => {
					const created_at = info.getValue();
					return (
						<span className="text-gray-500 text-end w-full inline-block">
							{created_at.toLocaleString()}
						</span>
					);
				},
			}),

			paymentColumnHelper.display({
				id: "actions",
				header: () => <span className="sr-only">Actions</span>,
				cell: (info) => {
					const { created_at, amount, payment_items } = info.row.original;
					return (
						<Dialog>
							<DialogTrigger
								render={<Button variant="link">View details</Button>}
							/>
							<DialogContent className="flex flex-col gap-6">
								<div className="flex flex-col gap-4">
									<DialogTitle>Payment details</DialogTitle>
									<DialogDescription>
										{formatCurrency("EUR", amount)} on{" "}
										{created_at.toLocaleDateString()}
									</DialogDescription>
								</div>
								<DataTable
									hideFooters
									columns={paymentItemColumns}
									data={payment_items}
								/>
								<div className="flex justify-end">
									<DialogClose
										render={<Button variant="outline">Dismiss</Button>}
									/>
								</div>
							</DialogContent>
						</Dialog>
					);
				},
			}),
		],
		[],
	);

	const paymentItemColumns = useMemo(
		() => [
			paymentItemColumnHelper.accessor("item_name", {
				id: "name",
				header: "Name",
				cell: (info) => {
					const row = info.row.original;
					const name = info.getValue();
					switch (row.item_type) {
						case "battle_entry":
							return <>Battle entry: {name}</>;
						case "subscription_purchase":
							// @ts-expect-error
							return <>Subscription: {m[`subscription_tier__${name}`]?.()}</>;
						case "bid_purchase":
							return <>Single bid: {name}</>;
						case "bid_package_purchase":
							return <>Bid package: {name}</>;
						default: {
							const _: never = row.item_type;
							throw new Error(`unexpected item_type: ${row.item_type}`);
						}
					}
				},
			}),
			paymentItemColumnHelper.accessor("quantity", {
				header: "Quantity",
				cell: (info) => {
					return info.getValue() ?? 1;
				},
			}),
		],
		[],
	);

	return (
		<>
			<DataTable
				hideFooters
				columns={paymentColumns}
				data={payments}
				pagination={pagination}
			/>
		</>
	);
}
